/* eslint-disable import/namespace */
import * as Sentry from '@sentry/react'

let lastNavigationSnapshot = null

const parseEnvBoolean = (value, fallback = false) => {
	if (value === undefined || value === null || value === '') {
		return fallback
	}

	const normalizedValue = String(value).trim().toLowerCase()

	if (['1', 'true', 'yes', 'on'].includes(normalizedValue)) {
		return true
	}

	if (['0', 'false', 'no', 'off'].includes(normalizedValue)) {
		return false
	}

	return fallback
}

export const isSentryEnabled =
	parseEnvBoolean(process.env.REACT_APP_SENTRY_ENABLED, true) &&
	!!process.env.REACT_APP_SENTRY_DSN

export const serializeData = data => {
	if (data === null || data === undefined) {
		return data
	}

	if (typeof data !== 'object') {
		return data
	}

	if (Array.isArray(data)) {
		return data
	}

	try {
		const seen = new WeakSet()
		return JSON.parse(
			JSON.stringify(data, (key, value) => {
				if (typeof value === 'object' && value !== null) {
					if (seen.has(value)) {
						return '[Circular]'
					}
					seen.add(value)
				}
				return value
			})
		)
	} catch (error) {
		return {
			_serializationError: error.message,
			_type: typeof data,
			_stringRepresentation: String(data),
		}
	}
}

const getTagValue = value => {
	if (value === null || value === undefined || value === '') {
		return null
	}

	if (
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean' ||
		typeof value === 'bigint'
	) {
		return String(value)
	}

	return null
}

const setTags = (setTagHandler, tags = {}) => {
	Object.entries(tags).forEach(([key, value]) => {
		const normalizedValue = getTagValue(value)
		if (normalizedValue !== null) {
			setTagHandler(key, normalizedValue)
		}
	})
}

const getActiveElementSnapshot = () => {
	try {
		const activeElement = document.activeElement

		if (!activeElement) {
			return null
		}

		return {
			tagName: activeElement.tagName || null,
			id: activeElement.id || null,
			className:
				typeof activeElement.className === 'string'
					? activeElement.className
					: null,
			role: activeElement.getAttribute?.('role') || null,
			ariaLabel: activeElement.getAttribute?.('aria-label') || null,
			textContent: activeElement.textContent?.trim()?.slice(0, 120) || null,
		}
	} catch (error) {
		return {
			error: error.message,
		}
	}
}

export const getDomSnapshot = () => {
	try {
		const rootElement = document.getElementById('root')

		return {
			readyState: document.readyState,
			visibilityState: document.visibilityState,
			historyLength: window.history.length,
			viewport: {
				width: window.innerWidth,
				height: window.innerHeight,
				devicePixelRatio: window.devicePixelRatio || 1,
			},
			rootChildCount: rootElement?.childNodes?.length ?? null,
			activeElement: getActiveElementSnapshot(),
		}
	} catch (error) {
		return {
			error: error.message,
		}
	}
}

export const getTenantIdFromPath = path => {
	const segments = path?.split('/').filter(Boolean) || []
	const tenantIndex = segments.indexOf('tenant')

	if (tenantIndex === -1) {
		return null
	}

	return segments[tenantIndex + 1] || null
}

export const getFeatureFromPath = path => {
	const segments = path?.split('/').filter(Boolean) || []
	const tenantIndex = segments.indexOf('tenant')

	if (tenantIndex === -1) {
		return segments[0] || 'app'
	}

	return segments[tenantIndex + 2] || 'dashboard'
}

export const getNavigationSnapshot = () => lastNavigationSnapshot

export const trackRouteTransition = ({
	fromPath,
	toPath,
	trigger = 'route_change',
	extra = {},
}) => {
	try {
		const snapshot = {
			timestamp: new Date().toISOString(),
			trigger,
			fromPath: fromPath || null,
			toPath: toPath || null,
			fromFeature: getFeatureFromPath(fromPath),
			toFeature: getFeatureFromPath(toPath),
			fromTenantId: getTenantIdFromPath(fromPath),
			toTenantId: getTenantIdFromPath(toPath),
			...serializeData(extra),
		}

		lastNavigationSnapshot = snapshot

		Sentry.setContext('navigation', snapshot)
		addUiBreadcrumb({
			category: 'navigation',
			message: 'route_transition',
			data: snapshot,
		})
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('[SENTRY] Failed to track route transition:', error)
	}
}

export const setSentryUserContext = (
	profile,
	auth,
	path = window.location.pathname
) => {
	if (!isSentryEnabled) {
		return
	}

	if (!profile) {
		return
	}

	const tenantIds = auth?.tenant_id || []
	const activeTenantId = getTenantIdFromPath(path) || tenantIds[0] || null

	try {
		Sentry.setUser({
			id: profile?.id?.toString() || 'unknown',
			username: profile?.username || 'unknown',
			email: profile?.email || undefined,
		})

		setTags(Sentry.setTag, {
			activeTenantId,
		})

		Sentry.setContext('auth', {
			tenantIds,
			currentPath: path,
		})
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('[SENTRY] Failed to set user context:', error)
	}
}

export const setSentryFeatureContext = ({
	feature,
	tenantId,
	path = window.location.pathname,
	extra = {},
}) => {
	if (!isSentryEnabled) {
		return
	}

	try {
		setTags(Sentry.setTag, {
			feature,
			activeTenantId: tenantId,
		})

		Sentry.setContext('feature', {
			feature: feature || 'unknown',
			tenantId: tenantId || null,
			path,
			lastNavigation: lastNavigationSnapshot,
			...serializeData(extra),
		})
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('[SENTRY] Failed to set feature context:', error)
	}
}

export const addUiBreadcrumb = ({
	category = 'ui',
	message,
	level = 'info',
	data = {},
}) => {
	if (!isSentryEnabled) {
		return
	}

	try {
		Sentry.addBreadcrumb({
			category,
			message,
			level,
			data: serializeData(data) || {},
		})
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('[SENTRY] Failed to add breadcrumb:', error)
	}
}

export const captureFeatureException = (
	error,
	{ feature, location, extra = {}, tags = {} } = {}
) => {
	if (!isSentryEnabled) {
		return
	}

	const captureError =
		error instanceof Error
			? error
			: new Error(
					location ? `Non-error captured in ${location}` : 'Non-error captured'
				)

	try {
		Sentry.withScope(scope => {
			setTags(scope.setTag.bind(scope), {
				feature,
				location,
				...tags,
			})

			scope.setContext('feature_capture', {
				location: location || 'unknown',
				lastNavigation: lastNavigationSnapshot,
				...serializeData(extra),
			})

			Sentry.captureException(captureError)
		})
	} catch (scopeError) {
		// eslint-disable-next-line no-console
		console.warn('[SENTRY] Failed to capture feature exception:', scopeError)
	}
}

export const captureFeatureMessage = (
	message,
	{ feature, location, level = 'info', extra = {}, tags = {} } = {}
) => {
	if (!isSentryEnabled) {
		return
	}

	try {
		Sentry.withScope(scope => {
			setTags(scope.setTag.bind(scope), {
				feature,
				location,
				...tags,
			})

			scope.setContext('feature_capture', {
				location: location || 'unknown',
				lastNavigation: lastNavigationSnapshot,
				...serializeData(extra),
			})

			Sentry.captureMessage(message, level)
		})
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('[SENTRY] Failed to capture feature message:', error)
	}
}
